import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import AdSenseUnit from "@/components/AdSenseUnit";
import ContentPageFooter from "@/components/ContentPageFooter";

const About = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-6 pb-24">
      <Helmet>
        <title>About Ayeris – Who We Are</title>
        <meta name="description" content="Learn about Ayeris, the swipe-based movie and TV discovery app. Meet the team and our mission to end endless scrolling." />
        <link rel="canonical" href="https://ayeris.tv/about" />
      </Helmet>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">{t("common.back", "Back")}</span>
      </button>

      <h1 className="text-2xl font-display font-bold mb-6">{t("about.title")}</h1>

      <div className="space-y-8 text-sm leading-relaxed text-secondary-foreground">
        <p dangerouslySetInnerHTML={{ __html: t("about.intro1") }} />
        <p className="text-foreground font-medium">{t("about.intro2")}</p>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-3">{t("about.howTitle")}</h2>
          <p className="mb-3">{t("about.howP1")}</p>
          <p dangerouslySetInnerHTML={{ __html: t("about.howP2") }} />
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-3">{t("about.togetherTitle")}</h2>
          <p className="mb-3">{t("about.togetherP1")}</p>
          <p dangerouslySetInnerHTML={{ __html: t("about.togetherP2") }} />
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-3">{t("about.disclosureTitle")}</h2>
          <div className="border-l-2 border-primary/40 pl-4 space-y-3">
            <p dangerouslySetInnerHTML={{ __html: t("about.disclosureP1") }} />
            <p>{t("about.disclosureP2")}</p>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-3">{t("about.nativeAppsTitle")}</h2>
          <p className="mb-3">{t("about.nativeAppsP1")}</p>
          <p className="mb-3">{t("about.nativeAppsP2")}</p>
          <p dangerouslySetInnerHTML={{ __html: t("about.nativeAppsP3") }} />
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-3">{t("about.supportTitle")}</h2>
          <p className="mb-3">{t("about.supportP1")}</p>
          <p className="mb-3">
            <strong className="text-foreground">{t("about.supportLocalisation")}</strong> {t("about.supportLocalisationP")}
          </p>
          <p>
            <strong className="text-foreground">{t("about.supportPrivacy")}</strong> {t("about.supportPrivacyP")}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-3">{t("about.contactTitle")}</h2>
          <p className="mb-4">{t("about.contactP1")}</p>
          <div className="space-y-2">
            <p>
              <strong className="text-foreground">{t("about.contactBugs")}</strong>{" "}
              <a href="mailto:support@ayeris.tv" className="text-primary hover:underline">support@ayeris.tv</a>
            </p>
            <p>
              <strong className="text-foreground">{t("about.contactFeedback")}</strong>{" "}
              <a href="mailto:feedback@ayeris.tv" className="text-primary hover:underline">feedback@ayeris.tv</a>
            </p>
          </div>
          <div className="mt-4 space-y-1">
            <p>{t("about.contactSocial")} <strong className="text-foreground">@AyerisTV</strong></p>
            <p>{t("about.contactFounder")} <strong className="text-foreground">@MrPeachUK</strong></p>
          </div>
        </section>

        <section className="border-t border-border pt-6">
          <p className="mb-3">{t("about.signOff")}</p>
          <p className="font-medium text-foreground">{t("about.muchLove")}</p>
          <p className="mt-2 text-foreground">
            <strong>{t("about.founderName")}</strong>
            <br />
            <span className="text-muted-foreground">{t("about.founderRole")}</span>
          </p>
        </section>
      </div>
      <AdSenseUnit adSlot="5004064879" className="my-6" />
      <ContentPageFooter currentPath="/about" />
    </div>
  );
};

export default About;
